import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

export interface PresetItem {
  id: number;
  presetCode: string;
  title: string;
  description: string;
  isActive: boolean;
}

export interface LobbyItem {
  instanceType: "banner" | "collection" | "tag" | "game" | "customWidget";
  instanceId: number;
  priorityOrder: number;
}

export interface LobbyConfig {
  presetId?: number;
  widgetType?: string;
  items: LobbyItem[];
  isActive?: boolean;
}

export interface LobbyEntityItem {
  id: number;
  title: string;
  image?: string;
}

export interface CashierCurrencyConfig {
  currency: string;
  defaultAmount: number;
  suggestedAmounts: number[];
}

export interface CashierPaymentGroup {
  groupKey: "fiat" | "crypto";
  methodIds: number[];
}

export interface CashierConfig {
  presetId?: number;
  currencies: CashierCurrencyConfig[];
  paymentGroups: CashierPaymentGroup[];
  isActive?: number;
  showCashierAfterRegistration?: boolean;
}

export interface PaymentMethodItem {
  id: number;
  title: string;
  image?: string;
  currencyType?: "fiat" | "crypto";
}

interface PresetsState {
  list: PresetItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: number[];
  payload?: PresetItem;
  initialPayload?: PresetItem;
  processingItemId?: PresetItem["id"];
  lobbyConfig?: LobbyConfig;

  isLobbyFetched: boolean;
  isLobbyFetching: boolean;

  collections: LobbyEntityItem[];
  tags: LobbyEntityItem[];
  banners: LobbyEntityItem[];
  games: LobbyEntityItem[];
  customWidgets: LobbyEntityItem[];

  cashierConfig?: CashierConfig;

  isCashierFetched: boolean;
  isCashierFetching: boolean;

  availablePaymentMethods: PaymentMethodItem[];
  activeCashierGeo: string;
  activeCashierCurrency: string;
}

interface GetPresetsListPayload {
  limit: number;
  offset: number;

  presetCode?: string;
  title?: string;
  isActive?: number;

  order?: {
    id?: string;
    presetCode?: string;
    title?: string;
    isActive?: string;
  };
}

const initialState: PresetsState = {
  list: [],

  activePage: 0,

  totalItems: 0,
  totalPages: 0,

  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,

  selectedItems: [],
  lobbyConfig: undefined,

  isLobbyFetched: false,
  isLobbyFetching: false,
  collections: [],
  tags: [],
  banners: [],
  games: [],
  customWidgets: [],

  cashierConfig: undefined,

  isCashierFetched: false,
  isCashierFetching: false,

  availablePaymentMethods: [],

  activeCashierGeo: "WW",
  activeCashierCurrency: "EUR",
};

interface GetPaymentMethodsPayload {
  geo: string;
  currency: string;
}

export const getPresetsList = createAsyncThunk(
  "presets/list",
  async (params: GetPresetsListPayload) => {
    try {
      const result = await instance.get(apiRoutes.presetsList, {
        params,
      });

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getPresetItem = createAsyncThunk(
  "presets/item",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.presetsItem}/${id}`);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const createPreset = createAsyncThunk<
  PresetItem,
  PresetItem,
  { rejectValue: ApiErrorPayload }
>("presets/create", async (payload, { rejectWithValue }) => {
  try {
    const result = await instance.post(apiRoutes.presetsCreate, {
      presetCode: payload.presetCode,
      title: payload.title,
      description: payload.description,
      isActive: payload.isActive,
    });

    return result.data;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updatePreset = createAsyncThunk<
  PresetItem,
  PresetItem,
  { rejectValue: ApiErrorPayload }
>("presets/update", async (payload, { rejectWithValue, getState }) => {
  try {
    const state = getState() as PresetsState | { presets: PresetsState };
    const initialPayload =
      "presets" in state ? state.presets.initialPayload : state.initialPayload;
    const textChanges = getChangedFields(
      initialPayload,
      payload,
      ["title", "description", "isActive"],
      {
        title: normalizeOptionalText,
        description: normalizeOptionalText,
      },
    );
    const result = await instance.put(
      `${apiRoutes.presetsUpdate}/${payload.id}`,
      {
        ...textChanges,
        isActive: payload.isActive,
      },
    );

    return result.data;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const deletePresetItem = createAsyncThunk(
  "presets/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.presetsDelete}/${id}`);

      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getLobbyConfig = createAsyncThunk(
  "presets/lobby",
  async (id: string | number) => {
    try {
      const result = await instance.get(
        `${apiRoutes.presetsItem}/${id}/widgets/lobby`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateLobbyConfig = createAsyncThunk(
  "presets/updateLobby",
  async ({ id, items }: { id: string | number; items: LobbyItem[] }) => {
    try {
      const result = await instance.put(
        `${apiRoutes.presetsItem}/${id}/widgets/lobby`,
        {
          items,
          isActive: 1,
        },
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCollectionsList = createAsyncThunk(
  "presets/collections",
  async () => {
    try {
      const result = await instance.get(apiRoutes.collectionsList, {
        params: {
          limit: 100,
          offset: 0,
        },
      });

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getTagsList = createAsyncThunk("presets/tags", async () => {
  try {
    const result = await instance.get(apiRoutes.tagsList, {
      params: {
        limit: 100,
        offset: 0,
      },
    });

    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const getBannersList = createAsyncThunk("presets/banners", async () => {
  try {
    const result = await instance.get(apiRoutes.bannersList, {
      params: {
        limit: 100,
        offset: 0,
      },
    });

    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const getGamesList = createAsyncThunk("presets/games", async () => {
  try {
    const result = await instance.get(apiRoutes.gamesWidgetList, {
      params: {
        limit: 100,
        offset: 0,
      },
    });

    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const getCustomWidgetsList = createAsyncThunk(
  "presets/customWidgets",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.customizedList}?limit=500&offset=0&isActive=1`,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCashierConfig = createAsyncThunk(
  "presets/cashier",
  async ({ id, geo }: { id: string | number; geo: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.presetsItem}/${id}/widgets/cashier`,
        {
          params: {
            geo,
          },
        },
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getPaymentMethodsList = createAsyncThunk(
  "presets/paymentMethods",
  async ({ geo, currency }: GetPaymentMethodsPayload) => {
    try {
      const result = await instance.post(apiRoutes.paymentMethodList, {
        limit: 100,
        offset: 0,
        transactionType: "deposit",
        currency: [currency],
        geo: [geo],
        configIsActive: 1,
        order: {
          id: "asc",
        },
      });

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateCashierConfig = createAsyncThunk<
  CashierConfig,
  {
    id: string | number;
    payload: CashierConfig;
  },
  { rejectValue: ApiErrorPayload }
>("presets/updateCashier", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const result = await instance.put(
      `${apiRoutes.presetsItem}/${id}/widgets/cashier`,
      payload,
    );

    return result.data;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const presetsSlice = createSlice({
  name: "presets",
  initialState,

  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },

    handleUpdateFields: (state, action: PayloadAction<Partial<PresetItem>>) => {
      state.payload = {
        ...state.payload,
        ...action.payload,
      } as PresetItem;
    },

    handleClearFields: (state) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },

    handleSelectItem: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleClearLobby: (state) => {
      state.lobbyConfig = undefined;
      state.isLobbyFetched = false;
    },
    handleSetActiveCashierGeo: (state, action: PayloadAction<string>) => {
      state.activeCashierGeo = action.payload;
    },

    handleSetActiveCashierCurrency: (state, action: PayloadAction<string>) => {
      state.activeCashierCurrency = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getPresetsList.fulfilled, (state, action) => {
      state.isListFetched = true;

      state.list = action.payload?.items ?? [];

      state.totalItems = action.payload?.totalItems ?? 0;

      state.totalPages = action.payload?.totalPages ?? 0;
    });

    builder.addCase(createPreset.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(createPreset.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });

    builder.addCase(createPreset.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updatePreset.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });

    builder.addCase(updatePreset.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(
              mergeExistingFields(item, action.meta.arg),
              action.payload,
            )
          : item,
      );
      state.processingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });

    builder.addCase(updatePreset.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(deletePresetItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });

    builder.addCase(deletePresetItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });

    builder.addCase(deletePresetItem.rejected, (state) => {
      state.processingItemId = undefined;
    });

    builder.addCase(getPresetItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
      state.initialPayload = action.payload ? { ...action.payload } : undefined;
    });
    builder.addCase(getLobbyConfig.pending, (state) => {
      state.isLobbyFetching = true;
    });

    builder.addCase(getLobbyConfig.fulfilled, (state, action) => {
      state.isLobbyFetching = false;
      state.isLobbyFetched = true;

      state.lobbyConfig = action.payload;
    });

    builder.addCase(getLobbyConfig.rejected, (state) => {
      state.isLobbyFetching = false;
    });
    builder.addCase(updateLobbyConfig.pending, (state) => {
      state.isLobbyFetching = true;
    });

    builder.addCase(updateLobbyConfig.fulfilled, (state, action) => {
      state.isLobbyFetching = false;

      state.lobbyConfig = action.payload;
    });

    builder.addCase(updateLobbyConfig.rejected, (state) => {
      state.isLobbyFetching = false;
    });
    builder.addCase(getCollectionsList.fulfilled, (state, action) => {
      state.collections = action.payload?.items ?? [];
    });

    builder.addCase(getTagsList.fulfilled, (state, action) => {
      state.tags = action.payload?.items ?? [];
    });

    builder.addCase(getBannersList.fulfilled, (state, action) => {
      state.banners = action.payload?.items ?? [];
    });

    builder.addCase(getGamesList.fulfilled, (state, action) => {
      state.games = action.payload?.items ?? [];
    });

    builder.addCase(getCustomWidgetsList.fulfilled, (state, action) => {
      state.customWidgets = action.payload?.items ?? [];
    });
    builder.addCase(getCashierConfig.pending, (state) => {
      state.isCashierFetching = true;
    });
    builder.addCase(getCashierConfig.fulfilled, (state, action) => {
      state.isCashierFetching = false;
      state.isCashierFetched = true;

      state.cashierConfig = action.payload;
    });
    builder.addCase(getCashierConfig.rejected, (state) => {
      state.isCashierFetching = false;
    });
    builder.addCase(updateCashierConfig.pending, (state) => {
      state.isCashierFetching = true;
    });
    builder.addCase(updateCashierConfig.fulfilled, (state, action) => {
      state.isCashierFetching = false;

      state.cashierConfig = action.payload;
    });
    builder.addCase(updateCashierConfig.rejected, (state) => {
      state.isCashierFetching = false;
    });
    builder.addCase(getPaymentMethodsList.pending, (state) => {
      state.isCashierFetching = true;
    });

    builder.addCase(getPaymentMethodsList.fulfilled, (state, action) => {
      state.isCashierFetching = false;

      state.availablePaymentMethods = action.payload?.items ?? [];
    });

    builder.addCase(getPaymentMethodsList.rejected, (state) => {
      state.isCashierFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleUpdateFields,
  handleClearFields,
  handleClearLobby,
  handleSetActiveCashierGeo,
  handleSetActiveCashierCurrency,
} = presetsSlice.actions;
